import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  addPerguntaApi,
  moverPerguntaApi,
  removePerguntaApi,
  updatePerguntaApi,
  type GgPerguntaDto,
} from "@/services/genteGestao";

const TIPO_LABEL: Record<GgPerguntaDto["tipo"], string> = {
  NPS_0_10: "NPS 0–10",
  ESCALA_1_5: "Escala 1–5",
  TEXTO: "Texto",
};

export function AbaPerguntas({
  pesquisaId,
  perguntas,
  bloqueado,
  onChanged,
}: {
  pesquisaId: string;
  perguntas: GgPerguntaDto[];
  bloqueado: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [novoTipo, setNovoTipo] = useState<GgPerguntaDto["tipo"]>("ESCALA_1_5");
  const [novoTexto, setNovoTexto] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function saveTexto(p: GgPerguntaDto, texto: string, obrigatoria: boolean) {
    await updatePerguntaApi(pesquisaId, p.id, { texto, obrigatoria });
    onChanged();
  }

  return (
    <div className="flex flex-col gap-3">
      {bloqueado && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          A pesquisa já recebeu respostas. As perguntas não podem mais ser alteradas — só título, descrição, datas e
          status.
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {perguntas.length === 0 && (
        <p className="text-sm text-slate-500">Nenhuma pergunta. Adicione ou crie a pesquisa a partir de um modelo.</p>
      )}
      <ul className="flex flex-col gap-2">
        {perguntas.map((p, idx) => (
          <li key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">#{p.ordem}</span>
              <span>{TIPO_LABEL[p.tipo]}</span>
              {p.indicador && <span>{p.indicador === "ENPS" ? "eNPS" : "NPS produto"}</span>}
              {p.dimensao && <span>{p.dimensao}</span>}
              <label className="ml-auto flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={p.obrigatoria}
                  disabled={bloqueado}
                  onChange={(e) => void saveTexto(p, p.texto, e.target.checked)}
                />
                Obrigatória
              </label>
            </div>
            <textarea
              className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm"
              rows={2}
              defaultValue={p.texto}
              disabled={bloqueado}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== p.texto) void saveTexto(p, v, p.obrigatoria);
              }}
            />
            {!bloqueado && (
              <div className="mt-2 flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={idx === 0}
                  onClick={() => void moverPerguntaApi(pesquisaId, p.id, "up").then(onChanged)}
                  leftIcon={<ChevronUp className="h-4 w-4" />}
                >
                  Subir
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={idx === perguntas.length - 1}
                  onClick={() => void moverPerguntaApi(pesquisaId, p.id, "down").then(onChanged)}
                  leftIcon={<ChevronDown className="h-4 w-4" />}
                >
                  Descer
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600"
                  onClick={() => {
                    void removePerguntaApi(pesquisaId, p.id)
                      .then(onChanged)
                      .catch(() => setError("Não foi possível remover."));
                  }}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                >
                  Remover
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {!bloqueado && (
        adding ? (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <Select
              label="Tipo"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value as GgPerguntaDto["tipo"])}
              options={[
                { value: "NPS_0_10", label: "NPS 0–10" },
                { value: "ESCALA_1_5", label: "Escala 1–5" },
                { value: "TEXTO", label: "Texto" },
              ]}
            />
            <div className="mt-2">
              <Input label="Texto" value={novoTexto} onChange={(e) => setNovoTexto(e.target.value)} />
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  void addPerguntaApi(pesquisaId, { tipo: novoTipo, texto: novoTexto, obrigatoria: true })
                    .then(() => {
                      setNovoTexto("");
                      setAdding(false);
                      onChanged();
                    })
                    .catch(() => setError("Não foi possível adicionar."));
                }}
              >
                Adicionar
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
            Adicionar pergunta
          </Button>
        )
      )}
    </div>
  );
}
